import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { TagLabel } from "@/components/TagLabel";
import { Pill } from "@/components/ui/Pill";

interface TagPillProps {
  name: string;
  color: string;
  selected?: boolean;
  count?: number;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  /** Not confirmed yet, so it has no color. */
  suggested?: boolean;
  /** Lets a suggestion be kept from here: a leading + keeps it, and the name still opens it. */
  onKeep?: () => void;
}

export function TagPill({
  name,
  color,
  selected,
  count,
  onClick,
  disabled = false,
  className,
  ariaLabel,
  suggested = false,
  onKeep,
}: TagPillProps) {
  if (onKeep) {
    return (
      <Pill className={cn("gap-0 p-0", className)}>
        <button
          type="button"
          onClick={onKeep}
          disabled={disabled}
          aria-label={`Add ${name}`}
          className="inline-flex h-(--control-height) w-9 shrink-0 items-center justify-center rounded-full text-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:text-muted-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={onClick} className="inline-flex min-h-(--control-height) min-w-(--control-height) items-center rounded-full pr-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <TagLabel name={name} color={color} size="xs" suggested />
        </button>
      </Pill>
    );
  }

  return (
    <Pill
      variant={selected ? "active" : "muted"}
      onClick={onClick}
      disabled={disabled}
      ariaLabel={ariaLabel}
      pressed={selected}
      className={className}
    >
      <TagLabel name={name} color={color} size="xs" suggested={suggested} />
      {selected && <X className="size-3.5 shrink-0" aria-hidden="true" />}

      {typeof count === "number" && (
        <span>
          {count}
        </span>
      )}
    </Pill>
  );
}
